import React, { Component } from 'react';
import { Box, Button, Checkbox, DropdownMenu, Flex, Menu, MenuItem, Text } from 'core/components';
import { inject, observer } from 'mobx-react';
import { Search } from 'core/components/table';
import { Label } from 'app/components/labels/Label';

@inject('$labels')
@observer
export default class LabelMapping extends Component {
  // use state to track which MenuItem components are active
  // since we do not necessarily want to apply all labels to all models selected
  // this also allows a single mounted component to toggle active labels as props.collection changes
  state = {
    active: {},
    c_id: '',
    lastUpdated: '',
    selectedSize: 0,
    initialLabelState: {},
    modifiedLabels: {}
  };

  static getDerivedStateFromProps(props, state) {
    const { collection, $labels } = props;
    const { selected, selectedSize } = collection;

    if (
      collection.id !== state.c_id ||
      $labels.labels.lastUpdated !== state.lastUpdated ||
      selectedSize !== state.selectedSize
    ) {
      const active =
        selectedSize !== 0
          ? selected.reduce((ids, model) => {
              model.labels.forEach((label) => {
                if (!ids[label.id]) {
                  ids[label.id] = { label, count: 0 };
                }
                ids[label.id].count += 1;
              });
              return ids;
            }, {})
          : {};

      $labels.companyLabels.selected = Object.values(active)
        .filter((selectedLabel) => selectedLabel.count === selectedSize)
        .map(({ label }) => label);

      const initialLabelState = {};
      $labels.companyLabels.map((label) => {
        initialLabelState[label.id] = {
          isSelected: label.isSelected,
          isIndeterminate: !label.isSelected && active[label.id]
        };
        return label;
      });

      return {
        active,
        c_id: collection.id,
        lastUpdated: $labels.labels.lastUpdated,
        selectedSize,
        initialLabelState,
        modifiedLabels: {}
      };
    }

    return null;
  }

  componentDidMount() {
    const { $labels } = this.props;
    $labels.labels.fetch({ force: true });
  }

  handleSearch = (e) => {
    const { $labels } = this.props;

    return $labels.companyLabels.filter(e.target.value);
  };

  handleLabelClick = (label, e) => {
    e.stopPropagation();

    this.setState((prevState) => ({
      modifiedLabels: {
        ...prevState.modifiedLabels,
        [label.id]: true
      }
    }));

    label.select({ multi: true, shiftKey: e.shiftKey });
  };

  handleApply = () => {
    const { collection, $labels, onLabelChange, type } = this.props;
    const { initialLabelState, modifiedLabels } = this.state;
    const selectedItems = collection.selected.map((model) => model.id || model.get('id'));

    const labelsToAdd = [];
    const labelsToRemove = [];

    $labels.companyLabels.map((label) => {
      if (modifiedLabels[label.id]) {
        const initialState = initialLabelState[label.id];

        // label is now selected but wasn't fully selected before, add it
        if (label.isSelected && (!initialState || !initialState.isSelected)) {
          labelsToAdd.push(label.id);
        }
        // label was initially selected/indeterminate, but was now unchecked, remove it from all selected items
        else if (!label.isSelected && initialState && (initialState.isSelected || initialState.isIndeterminate)) {
          labelsToRemove.push(label.id);
        }
      }
      return label;
    });

    let updatePromise = Promise.resolve();

    if (labelsToAdd.length > 0) {
      updatePromise = updatePromise.then(() => $labels.updateItems(type, 'INSERT', selectedItems, labelsToAdd));
    }

    if (labelsToRemove.length > 0) {
      updatePromise = updatePromise.then(() => $labels.updateItems(type, 'DELETE', selectedItems, labelsToRemove));
    }

    return updatePromise
      .then(() => $labels.labels.fetch())
      .then(() => {
        $labels.labels.setLastUpdated();
        return collection.fetch({ force: true });
      })
      .then(() => {
        if (onLabelChange) {
          onLabelChange(type, 'UPDATE', selectedItems);
        }
      });
  };

  handleCancel = () => {
    const { $labels } = this.props;
    $labels.labels.fetch({ force: true });
  };

  render() {
    const {
      collection,
      $labels,
      showSelected = true,
      selectedStr,
      minimal = false,
      text = 'Edit Labels',
      popoverProps,
      type
    } = this.props;
    const { active, modifiedLabels } = this.state;

    const pluralizedSelectedStr = `${selectedStr}${collection.selectedSize === 1 ? '' : 's'}`;

    if (!collection.selectedSize) {
      return null;
    }
    return (
      <DropdownMenu
        {...popoverProps}
        content={
          <Box>
            <Search collection={$labels.companyLabels} onChange={this.handleSearch} />
            <Menu p={1} height={300} width={300} overflow="auto">
              {$labels.getCompanyLabelsByType(type).map((label) => {
                // don't show indeterminate state if user unchecks label
                const showIndeterminate =
                  !label.isSelected && active[label.id] && !(modifiedLabels[label.id] && !label.isSelected);

                return (
                  <MenuItem
                    py="6px"
                    as={Checkbox}
                    style={{ display: 'flex', alignItems: 'flex-end' }}
                    key={`${label.get('id')}${label.isSelected}`}
                    disabled={$labels.labels.loading}
                    checked={label.isSelected}
                    indeterminate={showIndeterminate}
                    shouldDismissPopover={false}
                    label={<Label label={label} />}
                    onClick={(e) => this.handleLabelClick(label, e)}
                  />
                );
              })}
            </Menu>
            <Flex p={1} justifyContent="space-between">
              <Button intent="primary" text="Apply" className="bp4-popover-dismiss" onClick={this.handleApply} />
              <Button className="bp4-popover-dismiss" text="Cancel" onClick={this.handleCancel} />
            </Flex>
          </Box>
        }
      >
        <Flex alignItems="center">
          {showSelected && (
            <Text as={Box} mr={2} small>
              {collection.selectedSize} {pluralizedSelectedStr} selected
            </Text>
          )}
          <Button minimal={minimal} icon="tag" text={text} rightIcon="caret-down" ariaIsMenuButton />
        </Flex>
      </DropdownMenu>
    );
  }
}
